import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { defineStore } from 'pinia'

import { TransactionClass } from '@types'

import { ROUTE_NAME } from '@/const'

import { createBunchElement, duplicateBunchElement, isDirtyList, unsavedGridBlocker, unshiftBunchElement } from '../utils/helpers'
import { getCurrentDate } from '@/helpers/dates'

import { useMainStore } from '@/store/main'
import { useModalsStore } from '../modals'
import { useTransactionsStore } from './index'
import { useRepositoriesStore } from '../repositories/index'

export const useTransactionsBunchStore = defineStore(
  'transactions-bunch',
  () => {
    // INIT

    const mainStore = useMainStore()
    const modalsStore = useModalsStore()
    const transactionsStore = useTransactionsStore()
    const repositoryStore = useRepositoriesStore()

    const router = useRouter()

    const transactionsList = computed(() => transactionsStore.getList)

    // STORE

    const initFlag = ref(false)
    const list = ref<Map<string, TransactionClass>>(new Map())

    // ACTIONS

    const createElement = (data?: Record<string, any>) => createBunchElement(list, TransactionClass, {
      repository_id: repositoryStore.getCurrentId,
      date: getCurrentDate({}),
      ...data,
    }, false)

    const unshiftElement = (element: TransactionClass) => unshiftBunchElement(element, list)

    const duplicateElement = (id: string) => duplicateBunchElement(id, list, TransactionClass, false)

    // GETTERS

    const getList = computed(() => (list.value.size ? list.value : new Map()))

    const getElementById = (id: string) =>
      list.value.get(id) as TransactionClass

    const isDirty = computed(() => isDirtyList(list.value))

    // FILL STORE

    watch(transactionsList, value => {
      list.value = new Map()
      value.forEach(item => {
        const instance = new TransactionClass(item)
        list.value.set(item.id, instance)
      })
      initFlag.value = true
    })

    // ADD BLOCKER IF DIRTY

    watch(isDirty, value => {
      if (value) {
        mainStore.addBlocker(
          `${ROUTE_NAME.TRANSACTIONS}-hasChanges`,
          unsavedGridBlocker('transactions', () => {
            router.push({ name: ROUTE_NAME.TRANSACTIONS })
          }, () => {
            modalsStore.closeModals(TransactionClass)
            getList.value.forEach(item => {
              item.cancel()
            })
          }),
        )
      } else {
        mainStore.removeBlocker(`${ROUTE_NAME.TRANSACTIONS}-hasChanges`)
      }
    })

    const validateAll = () => {
      list.value.forEach(item => {
        item.validateData()
      })
    }

    // CLEAR STORE

    const clear = () => {
      initFlag.value = false
      list.value = new Map()
    }

    return {
      initFlag,

      createElement,
      unshiftElement,
      duplicateElement,

      getList,
      getElementById,
      isDirty,
      validateAll,

      clear,
    }
  },
)
